/**
 * React Query hooks for reunification data in ONE.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getActiveEvents,
  getAllEvents,
  getEventDetail,
  getEventStudents,
  getGuardianSelf,
  preVerifySelf,
  markArrived,
  checkInSelf,
  reportStudentSafeOffsite,
  getMyStudents,
  getNotifications,
  getUnreadCount,
  markNotificationRead,
} from '../services/reunification';

// --- Event Hooks ---

export function useActiveEvents() {
  return useQuery({
    queryKey: ['events', 'active'],
    queryFn: getActiveEvents,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useAllEvents() {
  return useQuery({
    queryKey: ['events', 'all'],
    queryFn: getAllEvents,
    staleTime: 60_000,
  });
}

export function useEventDetail(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event', eventId],
    queryFn: () => getEventDetail(eventId!),
    enabled: !!eventId,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

export function useEventStudents(eventId: string | undefined) {
  return useQuery({
    queryKey: ['event-students', eventId],
    queryFn: () => getEventStudents(eventId!),
    enabled: !!eventId,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

// --- Guardian Self Hooks ---

export function useGuardianSelf(eventId: string | undefined) {
  return useQuery({
    queryKey: ['guardian-self', eventId],
    queryFn: () => getGuardianSelf(eventId!),
    enabled: !!eventId,
    staleTime: 15_000,
    refetchInterval: 15_000,
  });
}

export function useMyStudents() {
  return useQuery({
    queryKey: ['my-students'],
    queryFn: getMyStudents,
    staleTime: 60_000,
  });
}

// --- Guardian Action Hooks ---

export function usePreVerifySelf(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => preVerifySelf(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-self', eventId] });
    },
  });
}

export function useMarkArrived(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => markArrived(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-self', eventId] });
    },
  });
}

export function useCheckInSelf(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => checkInSelf(eventId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['guardian-self', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useReportSafeOffsite(eventId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      studentId,
      data,
    }: {
      studentId: string;
      data: { location?: string; notes?: string };
    }) => reportStudentSafeOffsite(eventId, studentId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-students', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
  });
}

// --- Notification Hooks ---

export function useNotifications(
  filters?: { is_read?: boolean; search?: string; page?: number; page_size?: number },
) {
  return useQuery({
    queryKey: ['notifications', filters],
    queryFn: () => getNotifications(filters),
    staleTime: 30_000,
    refetchInterval: 60_000,
  });
}

export function useUnreadCount() {
  return useQuery({
    queryKey: ['unread-count'],
    queryFn: getUnreadCount,
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: markNotificationRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      queryClient.invalidateQueries({ queryKey: ['unread-count'] });
    },
  });
}
